import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { SUBSCRIPTION_TIER_STRIP_ROLES } from '@shared/user-roles';
import {
  mergeWithSubscriptionTierBundle,
  type SubscriptionTierBundleKey,
} from '@shared/subscription-tier-roles';

function readPortalUrlFromCallableData(data: unknown): string | undefined {
  if (data && typeof data === 'object' && 'portalUrl' in data) {
    const u = (data as { portalUrl?: unknown }).portalUrl;
    return typeof u === 'string' ? u : undefined;
  }
  return undefined;
}

function readSubscriptionCheckoutPayload(data: unknown): {
  success: boolean;
  error?: string;
  url?: string;
  subscriptionId?: string;
} {
  if (!data || typeof data !== 'object') {
    return { success: false, error: 'Invalid checkout response' };
  }
  const o = data as Record<string, unknown>;
  return {
    success: o.success === true,
    error: typeof o.error === 'string' ? o.error : undefined,
    url: typeof o.url === 'string' ? o.url : undefined,
    subscriptionId: typeof o.subscriptionId === 'string' ? o.subscriptionId : undefined,
  };
}

const TIER_ORDER: Record<string, number> = {
  free: 0,
  nobility: 1,
  royalty: 2,
  imperial: 3,
};

function normalizeTierValue(value: unknown): string {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized in TIER_ORDER ? normalized : 'free';
}

function normalizeStatusValue(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function normalizeRolesInput(roles: unknown): string[] {
  const roleArray = Array.isArray(roles) ? roles : [roles];
  return roleArray
    .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
    .map((role) => role.toUpperCase());
}

function stripBlockedSubscriptionRoles(roles: string[]): string[] {
  return roles.filter((role) => !SUBSCRIPTION_TIER_STRIP_ROLES.has(role));
}

/**
 * Subscription Service
 * Handles subscription creation, plan selection, billing, and Stripe integration
 * Orchestrates subscription flow from plan selection to successful subscription
 */
export class SubscriptionService {
  constructor(
    private subscriptionUseCase: any,
    private subscriptionRepository: any,
    private userRepository: any,
    private analyticsUseCase: any,
    private purchaseUseCase?: any // Will be injected as PurchaseUseCase
  ) {
    console.log('[SubscriptionService] Constructor - Initialized');
  }

  // ===== USER ROLE & SUBSCRIPTION MANAGEMENT =====

  /**
   * Update user roles and subscription based on subscription tier
   * Used both from admin panel and subscription return URLs
   */
  async updateUserRolesAndSubscription(requestData: {
    adminUserId: string;          // User performing the action (must be admin)
    targetUserId: string;         // User receiving the role changes
    roles: string[];              // New roles to assign
    subscriptionTier: string;     // Subscription tier (FREE, NOBILITY, ROYALTY, IMPERIAL)
    subscriptionId?: string;      // Optional subscription ID for return URL scenarios
    source: 'admin' | 'subscriptionReturn' | 'manual'; // Source of the update
  }): Promise<{
    success: boolean;
    updatedUser?: any;
    updatedSubscription?: any;
    error?: string;
  }> {
    console.log('[SubscriptionService.updateUserRolesAndSubscription] START - Request:', requestData);
    
    try {
      // 1. Validate admin permissions (if not from subscription return)
      if (requestData.source === 'admin') {
        console.log('[SubscriptionService.updateUserRolesAndSubscription] Validating admin permissions');
        const adminUser = await this.userRepository.getUserById(requestData.adminUserId);
        if (!adminUser) {
          throw new Error('Admin user not found');
        }
        
        // Check if admin has permission to update roles
        const adminRoles = normalizeRolesInput(
          Array.isArray(adminUser.roles)
            ? adminUser.roles
            : [adminUser.roles || adminUser.role].filter(Boolean)
        );
        const hasAdminPermission = adminRoles.some((role: string) => role === 'ADMIN');
        
        if (!hasAdminPermission) {
          throw new Error('Insufficient permissions: Only admins can update user roles');
        }
        console.log('[SubscriptionService.updateUserRolesAndSubscription] Admin validation passed:', adminUser.email);
      }

      // 2. Get target user
      console.log('[SubscriptionService.updateUserRolesAndSubscription] Getting target user');
      const targetUser = await this.userRepository.getUserById(requestData.targetUserId);
      if (!targetUser) {
        throw new Error('Target user not found');
      }

      // 3. Validate subscription tier and determine appropriate roles
      const validTiers = ['FREE', 'NOBILITY', 'ROYALTY', 'IMPERIAL'];
      if (!validTiers.includes(requestData.subscriptionTier.toUpperCase())) {
        throw new Error(`Invalid subscription tier: ${requestData.subscriptionTier}`);
      }

      // 4. Enhance roles based on subscription tier
      const enhancedRoles = this.enhanceRolesForSubscriptionTier(
        requestData.roles,
        requestData.subscriptionTier.toUpperCase(),
        requestData.source
      );
      console.log('[SubscriptionService.updateUserRolesAndSubscription] Enhanced roles:', enhancedRoles);

      // 5. Update user roles only. Subscription state lives in subscriptions/{uid} — do not put Stripe/subscription
      // objects or audit metadata on the user document (see firestore.rules).
      console.log('[SubscriptionService.updateUserRolesAndSubscription] Updating user roles');
      const userUpdateData = {
        roles: enhancedRoles,
        updatedAt: new Date(),
      };

      await this.userRepository.updateUser(requestData.targetUserId, userUpdateData);

      // 6. Canonical subscription docs (`subscriptions/{uid}`) are server-only in Firestore rules.
      // Role changes above stand; mirror subscription tier via Admin SDK / Cloud Functions when needed.
      const updatedSubscription = null;

      // 7. Get updated user data
      const updatedUser = await this.userRepository.getUserById(requestData.targetUserId);

      // 8. Track analytics (if not from return URL to avoid duplicate tracking)
      if (requestData.source !== 'subscriptionReturn') {
        try {
          await this.analyticsUseCase.trackUserActivity({
            userId: requestData.adminUserId,
            activityType: 'userRoleUpdate',
            activityData: {
              targetUserId: requestData.targetUserId,
              targetUserEmail: targetUser.email,
              oldRoles: Array.isArray(targetUser.roles) ? targetUser.roles : [targetUser.roles || targetUser.role].filter(Boolean),
              newRoles: enhancedRoles,
              newSubscriptionTier: requestData.subscriptionTier.toLowerCase(),
              source: requestData.source
            }
          });
        } catch (analyticsError) {
          console.warn('[SubscriptionService.updateUserRolesAndSubscription] Analytics tracking failed:', analyticsError);
          // Don't fail the main operation for analytics errors
        }
      }

      console.log('[SubscriptionService.updateUserRolesAndSubscription] SUCCESS - Updated user roles and subscription');
      return {
        success: true,
        updatedUser,
        updatedSubscription
      };

    } catch (error) {
      console.error('[SubscriptionService.updateUserRolesAndSubscription] ERROR:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Enhance roles based on subscription tier
   * Different subscription tiers unlock different role capabilities
   */
  private enhanceRolesForSubscriptionTier(
    baseRoles: string[],
    subscriptionTier: string,
    source: 'admin' | 'subscriptionReturn' | 'manual',
  ): string[] {
    console.log('[SubscriptionService.enhanceRolesForSubscriptionTier] Base roles:', baseRoles, 'Tier:', subscriptionTier, 'source:', source);
    
    // Normalize incoming roles and remove disallowed *_ADMIN assignments from subscription flows.
    const normalizedBaseRoles = stripBlockedSubscriptionRoles(normalizeRolesInput(baseRoles));
    // Only staff/admin can retain ADMIN; returning from checkout must never keep a bogus ADMIN on the user doc.
    const adminRolesToKeep =
      source === 'admin' ? normalizedBaseRoles.filter((role) => role === 'ADMIN') : [];
    const preservedIndependentRoles = normalizedBaseRoles.filter((role) =>
      role === "INSTRUCTOR" || role === "STUDENT"
    );
    const st = subscriptionTier.toUpperCase();
    const normalizedSt = st === 'ROYAL' ? 'ROYALTY' : st;
    const tierKey: SubscriptionTierBundleKey =
      normalizedSt === 'NOBILITY' ||
      normalizedSt === 'ROYALTY' ||
      normalizedSt === 'IMPERIAL'
        ? normalizedSt
        : 'FREE';
    const enhancedRoles = mergeWithSubscriptionTierBundle(tierKey, [
      ...adminRolesToKeep,
      ...preservedIndependentRoles,
    ]);

    if (!enhancedRoles.includes('USER')) {
      enhancedRoles.unshift('USER');
    }

    console.log('[SubscriptionService.enhanceRolesForSubscriptionTier] Enhanced roles:', enhancedRoles);
    return enhancedRoles;
  }

  /**
   * Handle successful subscription from return URL
   * This method is called when a user returns from Stripe checkout
   */
  async handleSuccessfulSubscriptionReturn(returnData: {
    userId: string;
    subscriptionId: string;
    subscriptionTier: string;
    sessionId?: string;
  }): Promise<{
    success: boolean;
    updatedUser?: any;
    error?: string;
  }> {
    console.log('[SubscriptionService.handleSuccessfulSubscriptionReturn] START - Return data:', returnData);
    
    try {
      // Get the user to determine their current roles
      const user = await this.userRepository.getUserById(returnData.userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Get current roles, default to USER if none
      const currentRoles = Array.isArray(user.roles) ? user.roles : [user.roles || user.role || 'USER'].filter(Boolean);
      if (currentRoles.length === 0) currentRoles.push('USER');

      // Update user roles and subscription based on the new subscription tier
      const result = await this.updateUserRolesAndSubscription({
        adminUserId: returnData.userId, // User is updating their own subscription
        targetUserId: returnData.userId,
        roles: currentRoles, // Keep existing roles, just enhance them
        subscriptionTier: returnData.subscriptionTier,
        subscriptionId: returnData.subscriptionId,
        source: 'subscriptionReturn'
      });

      if (result.success) {
        console.log('[SubscriptionService.handleSuccessfulSubscriptionReturn] SUCCESS - User subscription updated');
        return {
          success: true,
          updatedUser: result.updatedUser
        };
      } else {
        throw new Error(result.error || 'Failed to update user subscription');
      }

    } catch (error) {
      console.error('[SubscriptionService.handleSuccessfulSubscriptionReturn] ERROR:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // ===== SUBSCRIPTION PLAN SELECTION & CREATION =====

  /**
   * Create subscription from plan selection
   * This is the main method called when user clicks a plan
   */
  async createSubscriptionFromPlan(planData: {
    userId: string;
    planId: string;
    frequency: 'monthly' | 'yearly' | '6months';
    email?: string;
    couponCode?: string;
    flow?: 'registration' | 'subscription' | 'cart'; // Add flow parameter
  }): Promise<{
    success: boolean;
    subscriptionId?: string;
    checkoutUrl?: string;
    error?: string;
  }> {
    console.log('[SubscriptionService.createSubscriptionFromPlan] START - PlanData:', planData);
    
    try {
      // Step 1: Validate user exists
      console.log('[SubscriptionService.createSubscriptionFromPlan] Validating user:', planData.userId);
      const user = await this.userRepository.getUserById(planData.userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Step 2: Check for existing active subscription
      console.log('[SubscriptionService.createSubscriptionFromPlan] Checking for existing subscription');
      const existingSubscription = await this.subscriptionRepository.getSubscriptionByUserId(planData.userId);
      const existingStatus = String(existingSubscription?.status || '').toLowerCase();
      const existingTier = normalizeTierValue(
        existingSubscription?.subscriptionTier ?? existingSubscription?.planId
      );

      const requestedTier = normalizeTierValue(planData.planId);
      const requestedTierRank = TIER_ORDER[requestedTier];

      const repositoryIndicatesActivePaid =
        !!existingSubscription &&
        ['active', 'trialing'].includes(existingStatus) &&
        TIER_ORDER[existingTier] > TIER_ORDER.free;

      const hasActivePaidSubscription = repositoryIndicatesActivePaid;
      const currentTier = existingTier;
      const currentTierRank = TIER_ORDER[currentTier];

      if (hasActivePaidSubscription) {
        if (requestedTierRank > currentTierRank) {
          console.log(
            '[SubscriptionService.createSubscriptionFromPlan] Upgrade requested, opening Stripe portal in upgrade flow',
            { currentTier, requestedTier }
          );

          const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
          const portalResult = await createPortalSession({
            intent: 'upgrade',
            targetPlanId: planData.planId,
          });
          const portalUrl = readPortalUrlFromCallableData(portalResult.data);

          if (!portalUrl) {
            throw new Error('Unable to open subscription upgrade flow. Please try again.');
          }

          return {
            success: true,
            checkoutUrl: portalUrl,
            subscriptionId: existingSubscription?.id
          };
        }

        if (requestedTierRank < currentTierRank) {
          // Downgrade flow — also via Stripe Billing Portal subscription_update
          // flow. Same shape as upgrade; Stripe handles the proration/timing
          // policy from the dashboard config (typically: change at period end
          // for downgrades).
          console.log(
            '[SubscriptionService.createSubscriptionFromPlan] Downgrade requested, opening Stripe portal in update flow',
            { currentTier, requestedTier }
          );
          const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
          const portalResult = await createPortalSession({
            intent: 'upgrade', // shared portal flow handles up + down via subscription_update
            targetPlanId: planData.planId,
          });
          const portalUrl = readPortalUrlFromCallableData(portalResult.data);
          if (!portalUrl) {
            throw new Error('Unable to open subscription downgrade flow. Please try again.');
          }
          return {
            success: true,
            checkoutUrl: portalUrl,
            subscriptionId: existingSubscription?.id,
          };
        }

        console.log('[SubscriptionService.createSubscriptionFromPlan] Same paid plan selected, opening Stripe customer portal');
        const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
        const portalResult = await createPortalSession({});
        const portalUrl = readPortalUrlFromCallableData(portalResult.data);

        if (!portalUrl) {
          throw new Error('Unable to open subscription management portal. Please try again.');
        }

        return {
          success: true,
          checkoutUrl: portalUrl,
          subscriptionId: existingSubscription.id
        };
      }

      // Step 3: Create Stripe checkout session via cloud function
      console.log('[SubscriptionService.createSubscriptionFromPlan] Creating Stripe checkout session');
      const createCheckout = httpsCallable(functions, 'createSubscriptionCheckout');
      
      const checkoutData = {
        planId: planData.planId,
        userId: planData.userId,
        email: planData.email || user.email,
        frequency: planData.frequency,
        couponCode: planData.couponCode,
        flow: planData.flow || 'subscription' // Pass flow parameter, default to 'subscription'
      };

      const checkoutResult = await createCheckout(checkoutData);
      const checkoutResponse = readSubscriptionCheckoutPayload(checkoutResult.data);

      if (!checkoutResponse.success) {
        throw new Error(checkoutResponse.error || 'Failed to create checkout session');
      }

      // Step 4: Track analytics (temporarily disabled)
      console.log('[SubscriptionService.createSubscriptionFromPlan] Analytics tracking temporarily disabled');
      // TODO: Re-enable analytics after fixing undefined value issues
      // await this.analyticsUseCase.trackUserActivity(...);

      console.log('[SubscriptionService.createSubscriptionFromPlan] SUCCESS - Checkout URL:', checkoutResponse.url);
      return {
        success: true,
        checkoutUrl: checkoutResponse.url,
        subscriptionId: checkoutResponse.subscriptionId
      };

    } catch (error) {
      console.error('[SubscriptionService.createSubscriptionFromPlan] ERROR:', error);
      
      // Track failed attempt (temporarily disabled)
      console.log('[SubscriptionService.createSubscriptionFromPlan] Analytics tracking temporarily disabled for errors');
      // TODO: Re-enable analytics after fixing undefined value issues

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Legacy hook — canonical subscription docs must not be written from the browser
   * (`firestore.rules`: `subscriptions` is server-only). Use `verifySubscriptionSession` or webhooks.
   */
  async handleSuccessfulSubscription(subscriptionData: {
    userId: string;
    stripeSubscriptionId: string;
    planId: string;
    frequency: string;
    status: 'ACTIVE' | 'TRIALING';
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
  }): Promise<{
    success: boolean;
    subscriptionId?: string;
    error?: string;
  }> {
    console.warn(
      '[SubscriptionService.handleSuccessfulSubscription] Deprecated — use verifySubscriptionSession; payload:',
      subscriptionData.userId
    );
    return {
      success: false,
      error:
        'Subscription documents cannot be created from the client. Use the verifySubscriptionSession Cloud Function after Checkout.',
    };
  }

  // ===== SUBSCRIPTION MANAGEMENT =====

  /**
   * Get user's current subscription with details
   */
  async getUserSubscription(userId: string): Promise<{
    subscription: any | null;
    user: any | null;
    isActive: boolean;
    daysRemaining?: number;
    tier: string;
  }> {
    console.log('[SubscriptionService.getUserSubscription] START - UserId:', userId);
    
    try {
      // Get user and subscription data
      const [user, subscription] = await Promise.all([
        this.userRepository.getUserById(userId),
        this.subscriptionRepository.getSubscriptionByUserId(userId)
      ]);

      if (!user) {
        // Race after registration: auth listener may call this before the
        // Firestore users doc finishes writing. Returning a default free
        // shape keeps the UI alive — the next refresh succeeds. Throwing
        // escalates to Sentry as an unhandled "User not found" alert (noise).
        console.warn(`SubscriptionService.getUserSubscription: users/${userId} not found yet — returning default free state`);
        return {
          subscription: null,
          user: null,
          isActive: false,
          tier: 'FREE',
        };
      }

      const isActive = subscription && ['ACTIVE', 'TRIALING'].includes(subscription.status);
      let daysRemaining: number | undefined;

      if (subscription && subscription.currentPeriodEnd) {
        const endDate = subscription.currentPeriodEnd instanceof Date 
          ? subscription.currentPeriodEnd 
          : new Date(subscription.currentPeriodEnd.seconds * 1000);
        const now = new Date();
        daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      }

      const tier = normalizeTierValue(subscription?.subscriptionTier ?? subscription?.planId);

      console.log('[SubscriptionService.getUserSubscription] SUCCESS - Active:', isActive, 'Tier:', tier, 'Days remaining:', daysRemaining);
      return {
        subscription,
        user,
        isActive,
        daysRemaining,
        tier
      };

    } catch (error) {
      console.error('[SubscriptionService.getUserSubscription] ERROR:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(userId: string, cancelAtPeriodEnd: boolean = true): Promise<{
    success: boolean;
    error?: string;
  }> {
    console.log('[SubscriptionService.cancelSubscription] START - UserId:', userId, 'CancelAtPeriodEnd:', cancelAtPeriodEnd);
    
    try {
      // Get current subscription first
      const subscription = await this.subscriptionRepository.getSubscriptionByUserId(userId);
      
      // Use subscription use case for cancellation logic
      const result = await this.subscriptionUseCase.cancelSubscription(userId, cancelAtPeriodEnd);
      
      // Track cancellation (temporarily disabled)
      console.log('[SubscriptionService.cancelSubscription] Analytics tracking temporarily disabled');
      // TODO: Re-enable analytics after fixing undefined value issues

      console.log('[SubscriptionService.cancelSubscription] SUCCESS');
      return { success: true };

    } catch (error) {
      console.error('[SubscriptionService.cancelSubscription] ERROR:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Change subscription plan
   */
  async changeSubscriptionPlan(userId: string, newPlanData: {
    tier: 'free' | 'basic' | 'professional' | 'enterprise';
    frequency: 'monthly' | 'yearly';
    couponCode?: string;
  }): Promise<{
    success: boolean;
    subscription?: any;
    paymentRequired?: boolean;
    error?: string;
  }> {
    console.log('[SubscriptionService.changeSubscriptionPlan] START - UserId:', userId, 'NewPlan:', newPlanData);
    
    try {
      // Get current subscription first
      const subscription = await this.subscriptionRepository.getSubscriptionByUserId(userId);
      
      // Use subscription use case for plan change logic
      const result = await this.subscriptionUseCase.changeSubscription(userId, newPlanData);
      
      // Track plan change (temporarily disabled)
      console.log('[SubscriptionService.changeSubscriptionPlan] Analytics tracking temporarily disabled');
      // TODO: Re-enable analytics after fixing undefined value issues

      console.log('[SubscriptionService.changeSubscriptionPlan] SUCCESS - PaymentRequired:', result.paymentRequired);
      return {
        success: true,
        subscription: result.subscription,
        paymentRequired: result.paymentRequired
      };

    } catch (error) {
      console.error('[SubscriptionService.changeSubscriptionPlan] ERROR:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // ===== SUBSCRIPTION PLANS =====

  /**
   * Get all available subscription plans
   */
  async getSubscriptionPlans(): Promise<{
    plans: any[];
    success: boolean;
    error?: string;
  }> {
    console.log('[SubscriptionService.getSubscriptionPlans] START');
    
    try {
      // This would typically come from a plans repository, but for now use the use case
      const plans = await this.subscriptionUseCase.getAvailablePlans();
      
      console.log('[SubscriptionService.getSubscriptionPlans] SUCCESS - Found', plans.length, 'plans');
      return {
        success: true,
        plans
      };

    } catch (error) {
      console.error('[SubscriptionService.getSubscriptionPlans] ERROR:', error);
      return {
        success: false,
        plans: [],
        error: error instanceof Error ? error.message : 'Failed to load subscription plans'
      };
    }
  }

  // ===== PROMO CODES =====

  /**
   * Validate promo code via Stripe
   * Simple method to check if promo code exists and is valid
   */
  async validatePromoCode(code: string): Promise<{
    valid: boolean;
    code?: string;
    description?: string;
    discount?: string;
    error?: string;
  }> {
    console.log('[SubscriptionService.validatePromoCode] Validating code:', code);
    
    try {
      // For now, return the hardcoded promo codes
      // TODO: Replace with actual Stripe coupon validation
      const upperCode = code.toUpperCase();
      
      const validCoupons = {
        // 'DANCEREALMX2025': {
        //   code: 'DANCEREALMX2025',
        //   description: 'First 6 months free on any plan',
        //   discount: '100% off for first 6 months'
        // },
        'DRXDANCE2025': {
          code: 'DRXDANCE2025', 
          description: 'First 6 months free on Royalty plan',
          discount: '100% off for first 6 months'
        }
      };

      const coupon = validCoupons[upperCode as keyof typeof validCoupons];
      if (coupon) {
        console.log('[SubscriptionService.validatePromoCode] Valid coupon found:', coupon.code);
        return {
          valid: true,
          ...coupon
        };
      }

      console.log('[SubscriptionService.validatePromoCode] Invalid coupon code');
      return {
        valid: false,
        error: 'Invalid promo code'
      };

    } catch (error) {
      console.error('[SubscriptionService.validatePromoCode] ERROR:', error);
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Error validating promo code'
      };
    }
  }
}
